import { createAsyncThunk } from "@reduxjs/toolkit";
import {
	setBrandTeams,
	setEventLoading,
	setEvents,
	setFeaturedBrandTeam,
	setFeaturedBrandTeamDescription,
	setFeaturedRider,
	setFeaturedRiderDescription,
	setLeaderboardLoading,
	setRiders,
	setSeasons,
	setSelectedLeaderBoard,
	setSelectedLeague,
	setSelectedSeason,
	setSelectedSeasonById,
	setSelectedDiscipline,
	setSelectedTeam,
	setSeries,
	setSports,
	setTeamLoading,
	setVideos,
} from "../slices/fantasySlice";
import { getEvents } from "../../services/eventService";
import { getGlobalLeague, getLeague } from "../../services/leagueService";
import { getLeaderboard } from "../../services/leaderboardService";
import { getVideosByType } from "../../services/videoService";
import { getCurrentSeason, getSeasons } from "../../services/seasonService";
import { getRiders } from "../../services/riderService";
import { getBrandTeams } from "../../services/brandTeamService";
import { getSeries, getSports } from "../../services/series";
import {
	initialBrandTeam,
	initialRider,
	initialSeason,
	initialTeamComplete,
} from "../../types/model";
import { getTeams } from "../../services/teamService";
import { getFeaturedByType } from "../../services/featuredService";

type FeaturedContextInput = {
	sportId?: string;
	seriesId?: string;
};

export const getEventsThunk = createAsyncThunk(
	"fantasy/getEvents",
	async (_, thunkAPI) => {
		try {
			thunkAPI.dispatch(setEventLoading(true));
			const res = await getEvents();
			thunkAPI.dispatch(setEvents(res.data));
			return res.data;
		} catch (err) {
			console.error("Error fetching events:", err);
			return thunkAPI.rejectWithValue("Failed to fetch events");
		} finally {
			thunkAPI.dispatch(setEventLoading(false));
		}
	},
);

export const getGlobalLeaguesThunk = createAsyncThunk(
	"fantasy/getGlobalLeague",
	async (
		{
			sportId,
			seriesId,
			seasonId,
		}: { sportId: string; seriesId: string; seasonId?: string },
		thunkAPI,
	) => {
		try {
			const res = await getGlobalLeague(sportId, seriesId, seasonId);
			thunkAPI.dispatch(setSelectedLeague(res.data));
			return res.data;
		} catch (err) {
			console.error("Error fetching global league:", err);
			return thunkAPI.rejectWithValue("Failed to fetch global league");
		}
	},
);

export const getLeaderboardThunk = createAsyncThunk(
	"fantasy/getLeaderboard",
	async (leagueId: string, thunkAPI) => {
		try {
			if (!leagueId) {
				return thunkAPI.rejectWithValue("League ID is required");
			}

			thunkAPI.dispatch(setLeaderboardLoading(true));
			const res = await getLeaderboard(leagueId);
			if (!res.data) {
				return thunkAPI.rejectWithValue("No data found for leaderboard");
			}

			// Add a rank to the leaderboard.
			const sortedTeams = [...res.data.teams].sort(
				(a, b) => b.totalPoints - a.totalPoints,
			);
			const leaderboardWithRank = sortedTeams.map((team, index) => ({
				...team,
				rank: index + 1,
			}));
			res.data.teams = leaderboardWithRank;

			thunkAPI.dispatch(setSelectedLeaderBoard(res.data));
			return res.data;
		} catch (err) {
			console.error("Error fetching leaderboard:", err);
			return thunkAPI.rejectWithValue("Failed to fetch leaderboard");
		} finally {
			thunkAPI.dispatch(setLeaderboardLoading(false));
		}
	},
);

export const getVideosThunk = createAsyncThunk(
	"fantasy/fetchScrollbarVideos",
	async (_, thunkAPI) => {
		try {
			const res = await getVideosByType("scrollbar");
			if (res) {
				// Assuming you have an action to set scrollbar videos in your slice
				thunkAPI.dispatch(setVideos(res));
			}
			return res;
		} catch (error) {
			console.error("Error fetching scrollbar videos:", error);
			return thunkAPI.rejectWithValue("Failed to fetch scrollbar videos");
		}
	},
);

export const fetchCurrentSeasonThunk = createAsyncThunk(
	"fantasy/fetchCurrentSeason",
	async (_, thunkAPI) => {
		try {
			const res = await getCurrentSeason();
			if (res) {
				thunkAPI.dispatch(setSelectedSeason(res.data));
			}
			return res.data;
		} catch (error) {
			console.error("Error fetching current season:", error);
			return thunkAPI.rejectWithValue("Failed to fetch current season");
		}
	},
);

export const fetchFeaturedRiderThunk = createAsyncThunk(
	"fantasy/fetchFeaturedRider",
	async ({ sportId, seriesId }: FeaturedContextInput, thunkAPI) => {
		try {
			if (!sportId || !seriesId) {
				thunkAPI.dispatch(setFeaturedRider(initialRider));
				thunkAPI.dispatch(setFeaturedRiderDescription(""));
				return null;
			}

			const res = await getFeaturedByType("rider", { sportId, seriesId });
			thunkAPI.dispatch(setFeaturedRider(res?.data?.rider || initialRider));
			thunkAPI.dispatch(setFeaturedRiderDescription(res?.data?.description || ""));
			return res?.data;
		} catch (error) {
			console.error("Error fetching featured rider:", error);
			return thunkAPI.rejectWithValue("Failed to fetch featured rider");
		}
	},
);

export const fetchFeaturedBrandTeamThunk = createAsyncThunk(
	"fantasy/fetchFeaturedBrandTeam",
	async ({ sportId, seriesId }: FeaturedContextInput, thunkAPI) => {
		try {
			if (!sportId || !seriesId) {
				thunkAPI.dispatch(setFeaturedBrandTeam(initialBrandTeam));
				thunkAPI.dispatch(setFeaturedBrandTeamDescription(""));
				return null;
			}

			const res = await getFeaturedByType("brandTeam", { sportId, seriesId });
			thunkAPI.dispatch(
				setFeaturedBrandTeam(res?.data?.brandTeam || initialBrandTeam),
			);
			thunkAPI.dispatch(
				setFeaturedBrandTeamDescription(res?.data?.description || ""),
			);
			return res?.data;
		} catch (error) {
			console.error("Error fetching featured brand team:", error);
			return thunkAPI.rejectWithValue("Failed to fetch featured brand team");
		}
	},
);

export const fetchSportsThunk = createAsyncThunk(
	"fantasy/fetchSports",
	async (_, thunkAPI) => {
		try {
			const res = await getSports();
			thunkAPI.dispatch(setSports(res.data));
			return res.data;
		} catch (error) {
			console.error("Error fetching sports:", error);
			return thunkAPI.rejectWithValue("Failed to fetch sports");
		}
	},
);

export const fetchSeriesThunk = createAsyncThunk(
	"fantasy/fetchSeries",
	async (_, thunkAPI) => {
		try {
			const res = await getSeries();
			thunkAPI.dispatch(setSeries(res.data));
			return res.data;
		} catch (error) {
			console.error("Error fetching series:", error);
			return thunkAPI.rejectWithValue("Failed to fetch series");
		}
	},
);

export const fetchSeasonsThunk = createAsyncThunk(
	"fantasy/fetchSeasons",
	async (_, thunkAPI) => {
		try {
			const res = await getSeasons();
			if (res) {
				thunkAPI.dispatch(setSeasons(res.data));
				const activeSeason = res.data.find((season: any) => season.isActive);
				if (activeSeason) {
					thunkAPI.dispatch(setSelectedSeason(activeSeason));
				} else {
					const currentSeason = res.data[0]; // Fallback to the first season
					thunkAPI.dispatch(setSelectedSeason(currentSeason));
				}
			} else {
				thunkAPI.dispatch(setSelectedSeason(initialSeason));
			}
		} catch (error) {
			console.error("Error fetching seasons:", error);
			return thunkAPI.rejectWithValue("Failed to fetch seasons");
		}
	},
);

//Get a league with league ID
export const fetchLeagueThunk = createAsyncThunk(
	"fantasy/fetchLeague",
	async (leagueId: string, thunkAPI) => {
		try {
			const res = await getLeague(leagueId);
			if (res) {
				thunkAPI.dispatch(setSelectedLeague(res.data));
			}
			return res.data;
		} catch (error) {
			console.error("Error fetching league:", error);
			return thunkAPI.rejectWithValue("Failed to fetch league");
		}
	},
);

//Get a team with a league ID
export const fetchTeamWithLeagueIdThunk = createAsyncThunk(
	"fantasy/fetchTeam",
	async (leagueId: string, thunkAPI) => {
		try {
			thunkAPI.dispatch(setTeamLoading(true));
			const res = await getTeams({ leagueId });
			if (res) {
				thunkAPI.dispatch(setSelectedTeam(res.data[0]));
			} else {
				thunkAPI.dispatch(setSelectedTeam(initialTeamComplete));
			}
			return res.data;
		} catch (error) {
			console.error("Error fetching team:", error);
			return thunkAPI.rejectWithValue("Failed to fetch team");
		} finally {
			thunkAPI.dispatch(setTeamLoading(false));
		}
	},
);

export const fetchSeasonsDataThunk = createAsyncThunk(
	"fantasy/fetchSeasonsData",
	async (_, thunkAPI) => {
		try {
			const res = await getSeasons();

			if (res) {
				thunkAPI.dispatch(setSeasons(res.data));
			}

			return res.data;
		} catch (error) {
			console.error("Error fetching seasons:", error);
			return thunkAPI.rejectWithValue("Failed to fetch seasons");
		}
	},
);

export const handleViewLeagueThunk = createAsyncThunk(
	"fantasy/handleViewLeague",
	async (leagueId: string, thunkAPI) => {
		try {
			//We want to navigate to the league page. We also want to fetch the league data, reset the selected season, series and sport to this league.
			const res = await getLeague(leagueId);
			if (res) {
				thunkAPI.dispatch(setSelectedLeague(res.data));
				thunkAPI.dispatch(setSelectedSeasonById(res.data.season));
				thunkAPI.dispatch(
					setSelectedDiscipline({
						sport: res.data.sport,
						series: res.data.series,
					}),
				);
			}
			return res.data;
		} catch (error) {
			console.error("Error fetching league:", error);
			return thunkAPI.rejectWithValue("Failed to fetch league");
		}
	},
);

export const fetchRidersThunk = createAsyncThunk(
	"fantasy/fetchRiders",
	async (_, thunkAPI) => {
		try {
			const res = await getRiders();
			if (!res.data) return;
			thunkAPI.dispatch(setRiders(res.data));
			return res.data;
		} catch (error) {
			console.error("Error fetching riders:", error);
			return thunkAPI.rejectWithValue("Failed to fetch riders");
		}
	},
);

export const fetchBrandTeamsThunk = createAsyncThunk(
	"fantasy/fetchBrandTeams",
	async (_, thunkAPI) => {
		try {
			const res = await getBrandTeams();
			if (res) {
				thunkAPI.dispatch(setBrandTeams(res.data));
			}
			return res.data;
		} catch (error) {
			console.error("Error fetching brand teams:", error);
			return thunkAPI.rejectWithValue("Failed to fetch brand teams");
		}
	},
);
